/**
 * The shortlist above the register: the projects THIS operator pinned.
 *
 * ── WHY IT IS NOT A SORT ──────────────────────────────────────────────────
 *
 * The obvious build is "pinned projects sort first". It does not work here and
 * the reason is worth writing down, because it is the same trap the register's
 * own comments name for the progress and task columns: the list is SERVER-sorted
 * and PAGED. A client-side partition of the twenty-five rows on screen would
 * float the pins on page one and leave a pinned project sitting on page three,
 * with nothing on screen admitting it - and the operator would learn not to
 * trust the feature rather than learn about the paging.
 *
 * Sorting it server-side is the other option and is worse than it looks: the
 * sort would have to join a per-user table, `buildProjectOrderBy` takes no user
 * and is deliberately Prisma-free, and the ordering enum is mirrored on both
 * sides of the wire. That is a lot of machinery for a shortcut.
 *
 * A strip asks its own question and gets the same answer under every filter,
 * every search term and every page. It is also what the operator actually wants:
 * not "my projects near the top of a list I am scanning" but "my projects, one
 * click away, always in the same place".
 *
 * ── WHAT IT DOES NOT SHOW ─────────────────────────────────────────────────
 *
 * Archived projects, filtered out on the server. The strip is a shortcut to work
 * in progress and archiving is the act of saying a project is not that any more.
 * The pin row survives, so bringing the project back restores it here without
 * anybody re-pinning - and the count of those is printed rather than dropped in
 * silence, or an operator who pinned six and archived two sees four and cannot
 * tell why.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pin, PinOff } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { getPinnedProjects, onPinsChanged, unpinProject, type PinnedProject } from "../../../lib/projectPins";
import { Card, useToast } from "../../ui";

export function PinnedProjects({
  /**
   * Bumped by the list whenever a row menu pins or unpins something.
   *
   * A number rather than a callback pair, because the strip does not care WHICH
   * project changed - its whole content is one server answer, and the honest
   * response to "a pin moved somewhere" is to ask again. Threading the changed
   * row through would mean this component holding a second opinion about the
   * shortlist, which is the thing that goes stale.
   */
  epoch,
  onChanged,
}: {
  epoch: number;
  onChanged: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [rows, setRows] = useState<PinnedProject[] | null>(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getPinnedProjects();
      setRows(res.rows);
      setArchivedCount(res.archivedCount);
    } catch {
      // Silent, and it leaves whatever was on screen. This strip is a shortcut,
      // not the page: a red banner over the register because a convenience
      // failed to load would be louder than the thing it is reporting.
      setRows((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, epoch]);

  // The sidebar can unpin too (`shell/RailPins.tsx`), and it is on screen on
  // this page as well - so without this a chip stays here after the row it
  // mirrors has already gone from the rail, two drawings of one shortlist
  // disagreeing an inch apart. `epoch` cannot carry it: that number is bumped by
  // the register's own row menus, and the rail has no relationship to them.
  //
  // Re-asked rather than patched from the payload, because this card also counts
  // the pins the archive is holding and that number is not in the event.
  useEffect(() => onPinsChanged(() => void load()), [load]);

  // Nothing pinned, and nothing in the archive either: draw NOTHING. An empty
  // card headed "Pinned" on every operator's register would be permanent
  // furniture explaining a feature most of them have not used - the register is
  // the busiest screen in the product and it does not have the room.
  if (!rows || (rows.length === 0 && archivedCount === 0)) return null;

  async function unpin(id: string) {
    if (busy) return;
    setBusy(id);
    // Removed from the strip before the request lands. The pin is the operator's
    // own bookmark, the write cannot be refused for any reason but a network
    // failure, and a chip that lingers after the click reads as a button that
    // did not work. A failure puts it back by reloading.
    setRows((prev) => (prev ? prev.filter((p) => p.id !== id) : prev));
    try {
      await unpinProject(id);
      onChanged();
    } catch {
      toast(t("common.error"));
      await load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="crm-pinned">
      <span className="crm-pinned-lab">
        <Pin className="size-3.5" />
        {t("projects.pinnedTitle")}
      </span>

      {rows.map((p) => (
        <span key={p.id} className="crm-pin-chip">
          <Link to={`/projects/${p.id}`} className="crm-pin-chip-n crm-trunc">
            {p.name}
          </Link>
          {p.client ? <span className="crm-pin-chip-s crm-trunc">{p.client.name}</span> : null}
          <button
            type="button"
            className="crm-pin-chip-x"
            disabled={busy === p.id}
            title={t("projects.unpin")}
            aria-label={t("projects.unpin")}
            onClick={() => void unpin(p.id)}
          >
            <PinOff className="size-3.5" />
          </button>
        </span>
      ))}

      {/* Pins whose project is in the archive. Counted here rather than listed,
          because the strip is for work in progress - but said out loud, because
          a bookmark that silently stopped appearing is a bookmark nobody trusts
          again. */}
      {archivedCount > 0 ? (
        <span className="crm-mut text-[11.75px]">{t("projects.pinnedArchived", { n: archivedCount })}</span>
      ) : null}
    </Card>
  );
}
